import { Language, TranslationDictionary } from '../types';

export const languageNames: Record<Language, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  ja: '日本語',
  ko: '한국어',
  'zh-TW': '繁體中文',
};

export const translations: TranslationDictionary = {
  en: {
    appTitle: "FreightRate AI",
    appSubtitle: "Enterprise LTL Solutions",
    systemOp: "System Operational",
    heroTitle: "Smart Density Calculator",
    heroDesc: "Instantly calculate freight density and estimate NMFC class codes. Avoid carrier adjustments with our real-time math engine and AI-powered risk analysis.",
    // Form
    cargoDetails: "Cargo Details",
    addPallet: "Add Pallet",
    length: "Length",
    width: "Width",
    height: "Height",
    weight: "Weight",
    qty: "Qty",
    descOptional: "Description (Optional)",
    stackable: "Stackable?",
    remove: "Remove Pallet",
    // Results
    calcResults: "Calculation Results",
    livePreview: "LIVE PREVIEW",
    estClass: "Estimated Freight Class",
    density: "Density",
    linearFeet: "Linear Feet",
    totalWeight: "Total Weight",
    totalVol: "Total Volume",
    densityScale: "Density Scale",
    printBtn: "Print Calculation",
    capacityWarning: "⚠ May trigger capacity rules",
    disclaimerTitle: "Standard Disclaimer",
    disclaimerText: "This tool provides estimates based on density. Always verify with official NMFC publications.",
    // AI
    aiTitle: "AI Audit & Optimization",
    aiDesc: "Get an expert second opinion on potential classification risks and packing efficiency.",
    aiBtnStart: "Start AI Analysis",
    aiBtnAgain: "Re-Analyze Shipment",
    aiLoading: "Consulting logistics knowledge base...",
    aiPlaceholder: "Detailed analysis will appear here after calculation.",
    // Footer
    footerRights: "Empowering logistics with precision data.",
    footerSupport: "For support and enterprise API access.",
    footerLegal: "Disclaimer: Not a substitute for official carrier quotes.",
  },
  fr: {
    appTitle: "FreightRate AI",
    appSubtitle: "Solutions LTL Entreprise",
    systemOp: "Système Opérationnel",
    heroTitle: "Calculateur de Densité Intelligent",
    heroDesc: "Calculez instantanément la densité de fret et estimez les codes NMFC. Évitez les ajustements des transporteurs grâce à notre moteur de calcul et l'analyse des risques par IA.",
    cargoDetails: "Détails du Fret",
    addPallet: "Ajouter Palette",
    length: "Longueur",
    width: "Largeur",
    height: "Hauteur",
    weight: "Poids",
    qty: "Qté",
    descOptional: "Description (Optionnel)",
    stackable: "Gerbable ?",
    remove: "Supprimer",
    calcResults: "Résultats du Calcul",
    livePreview: "APERÇU EN DIRECT",
    estClass: "Classe NMFC Estimée",
    density: "Densité",
    linearFeet: "Pieds Linéaires",
    totalWeight: "Poids Total",
    totalVol: "Volume Total",
    densityScale: "Échelle de Densité",
    printBtn: "Imprimer le Calcul",
    capacityWarning: "⚠ Risque de règles de capacité",
    disclaimerTitle: "Avis de Non-responsabilité",
    disclaimerText: "Cet outil fournit des estimations basées sur la densité. Vérifiez toujours avec les publications officielles NMFC.",
    aiTitle: "Audit et Optimisation IA",
    aiDesc: "Obtenez un second avis d'expert sur les risques de classification et l'efficacité de l'emballage.",
    aiBtnStart: "Lancer l'Analyse IA",
    aiBtnAgain: "Ré-analyser l'Expédition",
    aiLoading: "Consultation de la base de connaissances logistique...",
    aiPlaceholder: "L'analyse détaillée apparaîtra ici après le calcul.",
    footerRights: "La logistique propulsée par la précision.",
    footerSupport: "Pour le support et l'accès API.",
    footerLegal: "Avis : Ne remplace pas les devis officiels des transporteurs.",
  },
  de: {
    appTitle: "FreightRate AI",
    appSubtitle: "LTL-Lösungen für Unternehmen",
    systemOp: "System Betriebsbereit",
    heroTitle: "Intelligenter Dichterechner",
    heroDesc: "Berechnen Sie sofort die Frachtdichte und schätzen Sie NMFC-Klassencodes. Vermeiden Sie Nachforderungen mit unserer Echtzeit-Berechnung und KI-Risikoanalyse.",
    cargoDetails: "Frachtdetails",
    addPallet: "Palette Hinzufügen",
    length: "Länge",
    width: "Breite",
    height: "Höhe",
    weight: "Gewicht",
    qty: "Menge",
    descOptional: "Beschreibung (Optional)",
    stackable: "Stapelbar?",
    remove: "Entfernen",
    calcResults: "Berechnungsergebnisse",
    livePreview: "LIVE-VORSCHAU",
    estClass: "Geschätzte Frachtklasse",
    density: "Dichte",
    linearFeet: "Lademeter (Linear Feet)",
    totalWeight: "Gesamtgewicht",
    totalVol: "Gesamtvolumen",
    densityScale: "Dichteskala",
    printBtn: "Berechnung Drucken",
    capacityWarning: "⚠ Kann Kapazitätsregeln auslösen",
    disclaimerTitle: "Haftungsausschluss",
    disclaimerText: "Dieses Tool liefert Schätzungen basierend auf der Dichte. Überprüfen Sie dies immer mit offiziellen NMFC-Publikationen.",
    aiTitle: "KI-Audit & Optimierung",
    aiDesc: "Holen Sie sich eine Expertenmeinung zu Klassifizierungsrisiken und Verpackungseffizienz.",
    aiBtnStart: "KI-Analyse Starten",
    aiBtnAgain: "Sendung Neu Analysieren",
    aiLoading: "Logistik-Wissensdatenbank wird konsultiert...",
    aiPlaceholder: "Die detaillierte Analyse erscheint hier nach der Berechnung.",
    footerRights: "Logistik mit Präzisionsdaten stärken.",
    footerSupport: "Für Support und API-Zugang.",
    footerLegal: "Haftungsausschluss: Kein Ersatz für offizielle Angebote.",
  },
  es: {
    appTitle: "FreightRate AI",
    appSubtitle: "Soluciones LTL Empresariales",
    systemOp: "Sistema Operativo",
    heroTitle: "Calculadora de Densidad Inteligente",
    heroDesc: "Calcule instantáneamente la densidad de carga y estime los códigos NMFC. Evite ajustes de transportistas con nuestro motor en tiempo real y análisis de riesgos por IA.",
    cargoDetails: "Detalles de Carga",
    addPallet: "Añadir Palet",
    length: "Largo",
    width: "Ancho",
    height: "Alto",
    weight: "Peso",
    qty: "Cant.",
    descOptional: "Descripción (Opcional)",
    stackable: "¿Apilable?",
    remove: "Eliminar",
    calcResults: "Resultados del Cálculo",
    livePreview: "VISTA PREVIA",
    estClass: "Clase de Flete Estimada",
    density: "Densidad",
    linearFeet: "Pies Lineales",
    totalWeight: "Peso Total",
    totalVol: "Volumen Total",
    densityScale: "Escala de Densidad",
    printBtn: "Imprimir Cálculo",
    capacityWarning: "⚠ Puede activar reglas de capacidad",
    disclaimerTitle: "Descargo de Responsabilidad",
    disclaimerText: "Esta herramienta proporciona estimaciones basadas en densidad. Verifique siempre con publicaciones oficiales de NMFC.",
    aiTitle: "Auditoría y Optimización IA",
    aiDesc: "Obtenga una segunda opinión experta sobre riesgos de clasificación y eficiencia de embalaje.",
    aiBtnStart: "Iniciar Análisis IA",
    aiBtnAgain: "Re-analizar Envío",
    aiLoading: "Consultando base de conocimientos logísticos...",
    aiPlaceholder: "El análisis detallado aparecerá aquí después del cálculo.",
    footerRights: "Potenciando la logística con datos precisos.",
    footerSupport: "Para soporte y acceso API.",
    footerLegal: "Aviso: No sustituye cotizaciones oficiales.",
  },
  ja: {
    appTitle: "FreightRate AI",
    appSubtitle: "企業向けLTLソリューション",
    systemOp: "システム稼働中",
    heroTitle: "スマート密度計算機",
    heroDesc: "貨物の密度を即座に計算し、NMFCクラスコードを推定します。リアルタイム計算エンジンとAIによるリスク分析で、運送業者による追加請求を回避します。",
    cargoDetails: "貨物詳細",
    addPallet: "パレット追加",
    length: "長さ",
    width: "幅",
    height: "高さ",
    weight: "重量",
    qty: "数量",
    descOptional: "品目説明 (任意)",
    stackable: "積み重ね可？",
    remove: "削除",
    calcResults: "計算結果",
    livePreview: "ライブプレビュー",
    estClass: "推定貨物クラス",
    density: "密度 (pcf)",
    linearFeet: "リニアフィート (荷台長)",
    totalWeight: "総重量",
    totalVol: "総容積",
    densityScale: "密度スケール",
    printBtn: "計算結果を印刷",
    capacityWarning: "⚠ 容量超過ルールの対象になる可能性があります",
    disclaimerTitle: "免責事項",
    disclaimerText: "このツールは密度に基づいた推定値を提供します。常に公式のNMFC出版物で確認してください。",
    aiTitle: "AI監査と最適化",
    aiDesc: "分類リスクと梱包効率に関する専門家のセカンドオピニオンを取得します。",
    aiBtnStart: "AI分析を開始",
    aiBtnAgain: "再分析する",
    aiLoading: "物流知識ベースを参照中...",
    aiPlaceholder: "詳細な分析は計算後にここに表示されます。",
    footerRights: "正確なデータで物流を強化。",
    footerSupport: "サポートおよびAPIアクセス。",
    footerLegal: "免責事項：運送業者の公式見積もりの代わりにはなりません。",
  },
  ko: {
    appTitle: "FreightRate AI",
    appSubtitle: "엔터프라이즈 LTL 솔루션",
    systemOp: "시스템 정상 가동",
    heroTitle: "스마트 화물 밀도 계산기",
    heroDesc: "화물 밀도를 즉시 계산하고 NMFC 등급 코드를 추정합니다. 실시간 계산 엔진과 AI 위험 분석을 통해 운송사의 요금 조정을 방지하세요.",
    cargoDetails: "화물 상세 정보",
    addPallet: "팔레트 추가",
    length: "길이",
    width: "너비",
    height: "높이",
    weight: "중량",
    qty: "수량",
    descOptional: "품목 설명 (선택 사항)",
    stackable: "적재 가능?",
    remove: "삭제",
    calcResults: "계산 결과",
    livePreview: "실시간 미리보기",
    estClass: "예상 화물 등급",
    density: "밀도",
    linearFeet: "차지 화물 길이 (Linear Feet)",
    totalWeight: "총 중량",
    totalVol: "총 부피",
    densityScale: "밀도 척도",
    printBtn: "계산 결과 인쇄",
    capacityWarning: "⚠ 용량 초과 규칙이 적용될 수 있습니다",
    disclaimerTitle: "면책 조항",
    disclaimerText: "이 도구는 밀도를 기반으로 한 추정치를 제공합니다. 항상 공식 NMFC 간행물을 확인하십시오.",
    aiTitle: "AI 감사 및 최적화",
    aiDesc: "분류 위험 및 포장 효율성에 대한 전문가의 2차 소견을 받으십시오.",
    aiBtnStart: "AI 분석 시작",
    aiBtnAgain: "화물 재분석",
    aiLoading: "물류 지식 베이스 상담 중...",
    aiPlaceholder: "계산 후 자세한 분석이 여기에 표시됩니다.",
    footerRights: "정밀 데이터로 물류 강화.",
    footerSupport: "지원 및 API 액세스 문의.",
    footerLegal: "면책 조항: 공식 운송사 견적을 대체하지 않습니다.",
  },
  'zh-TW': {
    appTitle: "FreightRate AI",
    appSubtitle: "企業級零擔運輸解決方案",
    systemOp: "系統運作中",
    heroTitle: "智能貨運密度計算器",
    heroDesc: "即時計算貨物密度並估算 NMFC 等級代碼。利用我們的實時數學引擎和 AI 風險分析，避免承運商的重新分級罰款。",
    cargoDetails: "貨物詳情",
    addPallet: "添加托盤",
    length: "長度",
    width: "寬度",
    height: "高度",
    weight: "重量",
    qty: "數量",
    descOptional: "貨物描述 (選填)",
    stackable: "可堆疊？",
    remove: "移除",
    calcResults: "計算結果",
    livePreview: "即時預覽",
    estClass: "預估運費等級 (Class)",
    density: "密度",
    linearFeet: "佔用車長 (Linear Feet)",
    totalWeight: "總重量",
    totalVol: "總體積",
    densityScale: "密度量表",
    printBtn: "列印計算結果",
    capacityWarning: "⚠ 可能觸發運力佔用規則",
    disclaimerTitle: "免責聲明",
    disclaimerText: "此工具僅提供基於密度的估算。請務必查閱官方 NMFC 出版物以獲取準確資訊。",
    aiTitle: "AI 審計與優化",
    aiDesc: "獲取關於分類風險和包裝效率的專家第二意見。",
    aiBtnStart: "開始 AI 分析",
    aiBtnAgain: "重新分析貨物",
    aiLoading: "正在諮詢物流知識庫...",
    aiPlaceholder: "詳細分析將在計算後顯示於此。",
    footerRights: "以精準數據賦能物流。",
    footerSupport: "技術支援與 API 接入。",
    footerLegal: "免責聲明：不可替代官方承運商報價。",
  }
};